/** Longest edge of the stored photo: plenty to read on a screen, a fraction of a camera original. */
const StoredEdge = 2000;

/** Photos already this small are stored as they are. */
const SmallEnough = 1_000_000;

/**
 * Pixels per section. The reader scales each image down to roughly a megapixel, so a whole camera
 * photo arrives with a receipt in it a few hundred pixels wide and its digits blur. Cut into
 * overlapping sections of about this size, every line is read close to the camera's resolution.
 */
const SectionPixels = 1_200_000;
const SectionOverlap = 0.08;
const MaxSections = 8;

/** Photos smaller than this are read whole. */
const SectionAbove = 2_500_000;

export interface PreparedReceipt {
  /** What is stored and shown. */
  photo: File;
  /** Overlapping sections, left to right then top to bottom, sent for reading only. */
  sections: File[];
}

/**
 * Gets a receipt photo ready to upload: a 3-5 MB camera image becomes a few hundred KB, and a long
 * receipt is also cut into overlapping sections so it can be read at full size. Anything that cannot
 * be decoded here (a PDF, an unusual format) is sent unchanged.
 */
export async function prepareReceipt(file: File): Promise<PreparedReceipt> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") {
    return { photo: file, sections: [] };
  }

  let bitmap: ImageBitmap;
  try {
    // Honours the photo's orientation, so a portrait shot is not handled sideways.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return { photo: file, sections: [] };
  }

  try {
    const name = file.name.replace(/\.[^.]+$/, "") || "receipt";
    const photo = file.size <= SmallEnough ? file : await shrink(bitmap, file, name);
    const sections =
      bitmap.width * bitmap.height > SectionAbove ? await slice(bitmap, name) : ([] as File[]);
    return { photo, sections };
  } catch {
    return { photo: file, sections: [] };
  } finally {
    bitmap.close();
  }
}

async function shrink(bitmap: ImageBitmap, original: File, name: string): Promise<File> {
  const scale = Math.min(1, StoredEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const blob = await draw(bitmap, 0, 0, bitmap.width, bitmap.height, width, height);
  if (!blob || blob.size >= original.size) return original;
  return new File([blob], `${name}.jpg`, {
    type: "image/jpeg",
    lastModified: original.lastModified,
  });
}

async function slice(bitmap: ImageBitmap, name: string): Promise<File[]> {
  const pixels = bitmap.width * bitmap.height;
  const count = Math.min(MaxSections, Math.ceil(pixels / SectionPixels));
  // Too big to cover at full size in the allowed sections: scale the whole photo down to fit.
  const scale = Math.min(1, Math.sqrt((MaxSections * SectionPixels) / pixels));

  // A grid shaped like the photo, so sections come out roughly square.
  const columns = Math.max(1, Math.round(Math.sqrt((count * bitmap.width) / bitmap.height)));
  const rows = Math.ceil(count / columns);
  const cellWidth = bitmap.width / columns;
  const cellHeight = bitmap.height / rows;
  const padX = cellWidth * SectionOverlap;
  const padY = cellHeight * SectionOverlap;

  const files: File[] = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const x = Math.max(0, column * cellWidth - padX);
      const y = Math.max(0, row * cellHeight - padY);
      const w = Math.min(bitmap.width - x, cellWidth + 2 * padX);
      const h = Math.min(bitmap.height - y, cellHeight + 2 * padY);
      const blob = await draw(bitmap, x, y, w, h, Math.round(w * scale), Math.round(h * scale));
      if (!blob) return [];
      const index = String(files.length + 1);
      files.push(new File([blob], `${name}-part${index}.jpg`, { type: "image/jpeg" }));
    }
  }
  return files;
}

function draw(
  bitmap: ImageBitmap,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  width: number,
  height: number,
): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return Promise.resolve(null);
  context.drawImage(bitmap, sx, sy, sw, sh, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
}
